import { afterNextRender, inject, Injectable, Injector, signal } from '@angular/core';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import { BehaviorSubject } from 'rxjs';

import { LoggerService } from '@spaces-ui/logger';

import { MdwEmoji } from '../models';

@Injectable({ providedIn: 'root' })
export class MdwEmojiService {
  private readonly loggerService = inject(LoggerService);
  private readonly injector = inject(Injector);

  public currentMessageId?: string;
  private _showEmojiPanel$ = new BehaviorSubject<boolean>(false);
  public showEmojiPanel$ = this._showEmojiPanel$.asObservable();
  public get panelIsShown() {
    return this._showEmojiPanel$.value;
  }
  private _initializeEmojiPanel$ = new BehaviorSubject<boolean>(false);
  public initializeEmojiPanel$ = this._initializeEmojiPanel$.asObservable();
  public panelPosition: { top: number; right: number } = { top: -9000, right: -9000 };

  /**
   * Extra class the panel host applies to the floating panel container for the
   * current invocation only (e.g. the calls UI pins its theme class so the
   * panel matches the call surface instead of the app theme). Cleared on hide.
   */
  public readonly panelClass = signal<string | null>(null);

  private onSelectEmoji?: (emoji: MdwEmoji) => void;
  private hideResetTimer?: ReturnType<typeof setTimeout>;

  public constructor() {
    polyfillCountryFlagEmojis(
      'Twemoji Country Flags',
      'https://shared-assets.medchatapp.com/fonts/TwemojiCountryFlags.woff2',
    );

    document.addEventListener('click', event => {
      if (
        this._showEmojiPanel$.value === false ||
        (event.target as HTMLElement).classList.contains('emoji-trigger')
      ) {
        return;
      }

      const emojiPanel = document.getElementById('floating-emoji-panel');
      const emojiTriggers = document.getElementsByClassName('emoji-trigger');
      if (!emojiPanel) {
        return;
      }

      const isInEmojiPanel = emojiPanel.contains(event.target as Node);
      const isInEmojiTrigger = Array.from(emojiTriggers).some(trigger =>
        trigger.contains(event.target as Node),
      );

      if (isInEmojiPanel || isInEmojiTrigger) {
        return;
      }

      this.hideEmojiPanel();
    });

    document.addEventListener(
      'keydown',
      event => {
        if (this._showEmojiPanel$.value && event.key === 'Escape') {
          event.stopPropagation();
          this.hideEmojiPanel();
        }
      },
      true,
    );
  }

  public handleSelectEmoji(emoji: MdwEmoji) {
    if (this.onSelectEmoji) {
      this.onSelectEmoji(emoji);
    }
  }

  public initializeEmojiPanel() {
    this._initializeEmojiPanel$.next(true);
  }

  public showPanel(
    messageId: string | undefined,
    event: MouseEvent,
    onSelectEmoji: (emoji: MdwEmoji) => void,
    isInline = false,
    customPosition?: { top: number; left: number; height: number; width: number },
    panelClass: string | null = null,
  ) {
    const isInitialized = this._initializeEmojiPanel$.value;
    const show = () => {
      // A reopen inside the previous hide's transition must not be moved off-screen by its reset.
      clearTimeout(this.hideResetTimer);
      this.currentMessageId = messageId;
      this.panelClass.set(panelClass);
      this.setPanelPosition(event, isInline, customPosition);
      this._showEmojiPanel$.next(true);
      this.onSelectEmoji = onSelectEmoji;
      setTimeout(() => this.focusSearch(), 0);
    };

    if (!isInitialized) {
      this.initializeEmojiPanel();
      // Wait for the render that creates #floating-emoji-panel: a bare setTimeout can beat it
      // on a slow machine, leaving the panel unpositioned (and off-screen) on first open.
      afterNextRender(() => show(), { injector: this.injector });
    } else {
      show();
    }
  }

  public getBaseName(emoji: string) {
    let baseName = emoji;
    if (baseName.includes('skin-tone')) {
      const baseNameParts = baseName.split(':');
      baseName = `:${baseNameParts[1]}:`;
    }

    return baseName;
  }

  public hideEmojiPanel() {
    this.clearSearch();
    this.currentMessageId = undefined;
    this._showEmojiPanel$.next(false);
    this.onSelectEmoji = undefined;

    // Reset position and theme class after the hide transition so the panel
    // doesn't visibly jump or re-theme while fading out.
    clearTimeout(this.hideResetTimer);
    this.hideResetTimer = setTimeout(() => {
      this.panelPosition = { top: -9000, right: -9000 };
      this.panelClass.set(null);
    }, 200);
  }

  private getPickerSearchInput(): HTMLInputElement | null {
    const picker = document.querySelector('em-emoji-picker');
    const shadowRoot = picker?.shadowRoot;

    if (!shadowRoot) {
      return null;
    }

    return shadowRoot.querySelector('input[type="search"]') as HTMLInputElement;
  }

  private focusSearch() {
    const searchInput = this.getPickerSearchInput();

    if (searchInput) {
      searchInput.focus();
    }
  }

  private clearSearch() {
    const searchInput = this.getPickerSearchInput();

    if (searchInput) {
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input'));
    }
  }

  private setPanelPosition(
    event: MouseEvent,
    isInline: boolean,
    customPosition?: { top: number; left: number; height: number; width: number },
  ) {
    const { clientY, clientX } = event;
    const { innerWidth, innerHeight } = window;
    const trigger = event.target as HTMLElement | null;
    const emojiPanel = document.getElementById('floating-emoji-panel');
    if (!emojiPanel || (!trigger && !customPosition)) {
      this.loggerService.error('Cannot position emoji panel: missing trigger and custom position');
      return;
    }

    const emojiPanelHeight = 468;
    const emojiPanelWidth = 384;
    const { top, left, height, width } =
      customPosition ??
      (trigger ? trigger.getBoundingClientRect() : { top: 0, left: 0, height: 0, width: 0 });
    const { scrollY } = window;

    const openLeft = innerWidth / 2 < clientX;

    const isPanelFitToBottom = innerHeight - clientY > emojiPanelHeight;
    const panelOffset = 10;

    let topPosition = isPanelFitToBottom
      ? top + height + scrollY + panelOffset
      : top - emojiPanelHeight + scrollY - panelOffset;

    const leftPosition = openLeft ? left + width - emojiPanelWidth : left;
    const rightPosition = innerWidth - leftPosition - emojiPanelWidth;

    if (topPosition < 64) {
      topPosition = 64;
    }

    if (topPosition + emojiPanelHeight > innerHeight - 24) {
      topPosition = innerHeight - emojiPanelHeight - 24;
    }

    this.panelPosition = {
      top: topPosition,
      right: isInline ? rightPosition : 16,
    };
  }
}
